<template>
  <div class="qbank-plan-page">
    <!-- Hero Section -->
    <div class="hero-section">
      <div class="hero-content">
        <div class="hero-icon">
          <i class="fas fa-credit-card"></i>
        </div>
        <h1 class="hero-title">{{ $t("qbank.plan.new_subscription") }}</h1>
        <p class="hero-subtitle">{{ $t("common.app_name") }}</p>
      </div>
      <div class="hero-decoration">
        <div class="circle circle-1"></div>
        <div class="circle circle-2"></div>
        <div class="circle circle-3"></div>
      </div>
    </div>

    <!-- Content -->
    <div class="plan-content-container">
      <div v-if="isloading" class="loading-state">
        <div class="loading-spinner">
          <div class="spinner-border text-success" role="status">
            <span class="sr-only"></span>
          </div>
        </div>
        <p class="loading-text">{{ $t("common.loading") }}</p>
      </div>

      <div v-else>
        <!-- Main Card -->
        <div class="main-card">
          <div class="card-header-custom">
            <div class="header-icon">
              <i class="fas fa-list-alt"></i>
            </div>
            <div class="header-text">
              <h3>{{ $t("qbank.plan.new_subscription") }}</h3>
              <p>{{ $t("qbank.plan.pick_category_hint") }}</p>
            </div>
          </div>
          <div class="card-body-custom">
            <!-- Categories Select -->
            <div class="selects-row">
              <div class="select-group">
                <label for="categoriesSelect" class="select-label">
                  <i class="fas fa-folder"></i>
                  {{ $t("qbank.plan.categories") }}
                </label>
                <select
                  id="categoriesSelect"
                  class="custom-select"
                  v-model="selectedCategory"
                  @change="filterSubCategories"
                >
                  <option value="" disabled>
                    {{ $t("qbank.plan.choose_category") }}
                  </option>
                  <option
                    v-for="category in categories"
                    :key="category.id"
                    :value="category.id"
                  >
                    {{ category.title }}
                  </option>
                </select>
              </div>

              <!-- Subcategories Select -->
              <div class="select-group">
                <label for="subCategoriesSelect" class="select-label">
                  <i class="fas fa-folder-open"></i>
                  {{ $t("qbank.plan.subcategories") }}
                </label>
                <select
                  id="subCategoriesSelect"
                  class="custom-select"
                  v-model="selectedSubCategory"
                  @change="fetchSubscriptions"
                >
                  <option value="" disabled>
                    {{ $t("qbank.plan.choose_subcategory") }}
                  </option>
                  <option
                    v-for="subCategory in subCategories"
                    :key="subCategory.id"
                    :value="subCategory.id"
                  >
                    {{ subCategory.title }}
                  </option>
                  <option v-if="subCategories.length === 0" disabled>
                    {{ $t("qbank.plan.no_subcategories") }}
                  </option>
                </select>
              </div>
            </div>

            <!-- Display Subscription Plans -->
            <div class="plans-section">
              <h4 v-if="subscriptionPlans.data.length != 0" class="plans-title">
                <i class="fas fa-tags"></i>
                {{ $t("qbank.plan.subscription_plans") }}
              </h4>
              <div class="plans-grid">
                <div v-if="isLoading" class="plans-loading">
                  <div class="spinner-border text-success" role="status">
                    <span class="visually-hidden">{{
                      $t("common.loading")
                    }}</span>
                  </div>
                  <p>{{ $t("qbank.plan.loading_plans") }}</p>
                </div>
                <div
                  v-else-if="
                    subscriptionPlans.data.length === 0 &&
                    subCategories.length != 0
                  "
                  class="plans-empty"
                >
                  <i class="fas fa-inbox"></i>
                  <p>{{ $t("qbank.plan.no_plans") }}</p>
                </div>
                <div
                  v-else
                  v-for="plan in subscriptionPlans.data"
                  :key="plan.id"
                  class="plan-card"
                >
                  <div class="plan-header">
                    <h5 class="plan-name">{{ plan.name }}</h5>
                    <div class="plan-price">
                      <span class="price-value" v-if="plan.price > 0">{{
                        plan.price
                      }}</span>
                      <span class="price-value free" v-else>{{
                        $t("common.free")
                      }}</span>
                      <span class="price-currency" v-if="plan.price > 0">{{
                        $appCurrency
                      }}</span>
                    </div>
                  </div>
                  <div class="plan-details">
                    <div class="detail-item">
                      <i class="fas fa-clock"></i>
                      <span class="detail-label">{{
                        $t("qbank.plan.duration")
                      }}</span>
                      <span class="detail-value"
                        >{{ plan.duration }} {{ $t("qbank.plan.days") }}</span
                      >
                    </div>
                    <div class="detail-item">
                      <i class="fas fa-gift"></i>
                      <span class="detail-label">{{
                        $t("qbank.plan.free_trial")
                      }}</span>
                      <span
                        class="detail-badge"
                        :class="plan.is_free_trial ? 'yes' : 'no'"
                      >
                        {{
                          plan.is_free_trial
                            ? $t("common.yes")
                            : $t("common.no")
                        }}
                      </span>
                    </div>
                    <div class="detail-item">
                      <i class="fas fa-check-circle"></i>
                      <span class="detail-label">{{
                        $t("common.status")
                      }}</span>
                      <span
                        class="detail-badge"
                        :class="plan.is_active ? 'active' : 'inactive'"
                      >
                        {{
                          plan.is_active
                            ? $t("common.active")
                            : $t("common.inactive")
                        }}
                      </span>
                    </div>
                  </div>
                  <button
                    class="subscribe-btn"
                    @click="confirmSubscription(plan.id)"
                  >
                    <i class="fas fa-check"></i>
                    <span>{{ $t("qbank.plan.subscribe_now") }}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Swal from "sweetalert2";
import { apiUrl } from "@/utils/api";
export default {
  name: "newPlan",
  data() {
    return {
      isLoading: false,

      categories: [],
      subCategories: [],
      subscriptionPlans: { data: [] },
      isloading: true,
      token: localStorage.getItem("access_token"),
      selectedCategory: "",
      selectedSubCategory: "",
    };
  },
  mounted() {
    this.fetchCategories();
  },
  methods: {
    async fetchCategories() {
      try {
        const response = await fetch(`${apiUrl}/qbanks/categories`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${this.token}`,
            "Content-Type": "application/json",
          },
        });

        if (response.ok) {
          const data = await response.json();
          this.categories = data.data;
          this.isloading = false;
        } else {
          console.error("Failed to fetch categories");
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
        this.isloading = false;
      }
    },
    filterSubCategories() {
      const selectedCategory = this.categories.find(
        (category) => category.id === this.selectedCategory
      );
      this.subCategories = selectedCategory
        ? selectedCategory.sub_categories
        : [];
      this.selectedSubCategory = "";
    },
    async fetchSubscriptions() {
      if (!this.selectedSubCategory) return;
      this.isLoading = true;
      try {
        const response = await fetch(
          `${apiUrl}/qbanks/sub-categories/${this.selectedSubCategory}/subscription-plans`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${this.token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          this.subscriptionPlans = data;
          this.isLoading = false;
        } else {
          console.error("Failed to fetch subscription plans");
          this.subscriptionPlans = { data: [] };
          this.isLoading = false;
        }
      } catch (error) {
        console.error("Error fetching subscription plans:", error);
        this.subscriptionPlans = { data: [] };
        this.isLoading = false;
      }
    },
    async confirmSubscription(planId) {
      const result = await Swal.fire({
        title: this.$t("qbank.plan.confirm_title"),
        text: this.$t("qbank.plan.confirm_text"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#00c744",
        cancelButtonColor: "#f46a6a",
        cancelButtonText: this.$t("common.cancel"),
        confirmButtonText: this.$t("common.ok"),
        customClass: {
          popup: "beautiful-swal-popup",
          title: "beautiful-swal-title",
          htmlContainer: "beautiful-swal-text",
          confirmButton: "beautiful-swal-confirm",
          cancelButton: "beautiful-swal-cancel",
        },
        backdrop: `rgba(0, 0, 0, 0.4)`,
      });

      if (result.isConfirmed) {
        try {
          const response = await fetch(
            `${apiUrl}/profile/qbanks/subscription-plans/${planId}/subscribe`,
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${this.token}`,
                "Content-Type": "application/json",
              },
            }
          );

          if (response.ok) {
            Swal.fire({
              title: this.$t("qbank.plan.subscribe_success"),
              icon: "success",
              confirmButtonText: this.$t("common.ok"),
              confirmButtonColor: "#00c744",
              customClass: {
                popup: "beautiful-swal-popup",
                title: "beautiful-swal-title",
                confirmButton: "beautiful-swal-confirm",
              },
              backdrop: `rgba(0, 0, 0, 0.4)`,
            });
          } else {
            const error = await response.json();

            // Check if user is already subscribed
            if (
              error.message &&
              error.message.toLowerCase().includes("already subscribed")
            ) {
              Swal.fire({
                title: "Already Subscribed",
                html: `
                                    <div style="text-align: center; padding: 10px 0;">
                                        <p style="font-size: 1rem; color: #64748b; margin-bottom: 16px; line-height: 1.6;">
                                            You're already subscribed to this plan.
                                        </p>
                                        <p style="font-size: 0.9rem; color: #94a3b8;">
                                            You can manage your subscription or choose a different plan.
                                        </p>
                                    </div>
                                `,
                icon: "info",
                confirmButtonText: this.$t("common.ok"),
                confirmButtonColor: "#00c744",
                customClass: {
                  popup: "beautiful-swal-popup already-subscribed-popup",
                  title: "beautiful-swal-title",
                  htmlContainer: "beautiful-swal-html",
                  confirmButton: "beautiful-swal-confirm",
                },
                backdrop: `rgba(0, 0, 0, 0.4)`,
              });
            } else if (
              error.message &&
              error.message
                .toLowerCase()
                .includes("already used the free trial")
            ) {
              Swal.fire({
                title: "Free Trial Already Used",
                html: `
                                    <div style="text-align: center; padding: 10px 0;">
                                        <p style="font-size: 1rem; color: #64748b; margin-bottom: 16px; line-height: 1.6;">
                                            You've already used your free trial for this plan.
                                        </p>
                                        <p style="font-size: 0.9rem; color: #94a3b8;">
                                            Please choose a paid subscription to continue.
                                        </p>
                                    </div>
                                `,
                icon: "info",
                confirmButtonText: this.$t("common.ok"),
                confirmButtonColor: "#00c744",
                customClass: {
                  popup: "beautiful-swal-popup free-trial-used-popup",
                  title: "beautiful-swal-title",
                  htmlContainer: "beautiful-swal-html",
                  confirmButton: "beautiful-swal-confirm",
                },
                backdrop: `rgba(0, 0, 0, 0.4)`,
              });
            } else {
              Swal.fire({
                title: this.$t("common.error"),
                text:
                  error.message ||
                  this.$t("qbank.plan.subscribe_error_generic"),
                icon: "error",
                confirmButtonText: this.$t("common.ok"),
                confirmButtonColor: "#f46a6a",
                customClass: {
                  popup: "beautiful-swal-popup",
                  title: "beautiful-swal-title",
                  htmlContainer: "beautiful-swal-text",
                  confirmButton: "beautiful-swal-confirm",
                },
                backdrop: `rgba(0, 0, 0, 0.4)`,
              });
            }
          }
        } catch (error) {
          Swal.fire({
            title: this.$t("common.error"),
            text: this.$t("qbank.plan.subscribe_error_fatal"),
            icon: "error",
            confirmButtonText: this.$t("common.ok"),
            confirmButtonColor: "#f46a6a",
            customClass: {
              popup: "beautiful-swal-popup",
              title: "beautiful-swal-title",
              htmlContainer: "beautiful-swal-text",
              confirmButton: "beautiful-swal-confirm",
            },
            backdrop: `rgba(0, 0, 0, 0.4)`,
          });
        }
      }
    },
  },
};
</script>

<style scoped>
/* ========== Main Page ========== */
.qbank-plan-page {
  background: linear-gradient(180deg, #f8fffe 0%, #f0fdf4 100%);
  min-height: 100vh;
}

/* ========== Hero Section ========== */
.hero-section {
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  padding: 50px 20px;
  text-align: center;
  position: relative;
  overflow: hidden;
  border-radius: 0 0 30px 30px;
  margin-bottom: 30px;
  box-shadow: 0 10px 40px rgba(0, 199, 68, 0.3);
}

.hero-content {
  position: relative;
  z-index: 2;
}

.hero-icon {
  width: 80px;
  height: 80px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 15px;
  backdrop-filter: blur(10px);
  animation: float 3s ease-in-out infinite;
}

.hero-icon i {
  font-size: 35px;
  color: white;
}

.hero-title {
  color: white;
  font-size: 2rem;
  font-weight: 800;
  margin-bottom: 8px;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.hero-subtitle {
  color: rgba(255, 255, 255, 0.9);
  font-size: 1rem;
  font-weight: 400;
}

.hero-decoration {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
}

.circle {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
}

.circle-1 {
  width: 150px;
  height: 150px;
  top: -40px;
  right: -40px;
  animation: pulse 4s ease-in-out infinite;
}

.circle-2 {
  width: 100px;
  height: 100px;
  bottom: -20px;
  left: 10%;
  animation: pulse 5s ease-in-out infinite 1s;
}

.circle-3 {
  width: 70px;
  height: 70px;
  top: 30%;
  left: 5%;
  animation: pulse 6s ease-in-out infinite 2s;
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

@keyframes pulse {
  0%,
  100% {
    transform: scale(1);
    opacity: 0.5;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.3;
  }
}

/* ========== Content Container ========== */
.plan-content-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px 40px;
}

/* ========== Loading State ========== */
.loading-state {
  background: white;
  border-radius: 20px;
  padding: 60px 30px;
  text-align: center;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.05);
}

.loading-spinner .spinner-border {
  width: 50px;
  height: 50px;
}

.loading-text {
  margin-top: 15px;
  color: #00c744;
  font-weight: 600;
}

/* ========== Main Card ========== */
.main-card {
  background: white;
  border-radius: 24px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.card-header-custom {
  background: linear-gradient(135deg, #f8fffe 0%, #f0fdf4 100%);
  padding: 24px 28px;
  display: flex;
  align-items: center;
  gap: 16px;
  border-bottom: 1px solid rgba(0, 196, 68, 0.1);
}

.header-icon {
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 6px 20px rgba(0, 199, 68, 0.3);
}

.header-icon i {
  font-size: 22px;
  color: white;
}

.header-text h3 {
  color: #2d3748;
  font-size: 1.3rem;
  font-weight: 700;
  margin: 0 0 4px 0;
}

.header-text p {
  color: #718096;
  font-size: 0.9rem;
  margin: 0;
}

.card-body-custom {
  padding: 28px;
}

/* ========== Selects Row ========== */
.selects-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.select-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.select-label {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #2d3748;
  font-weight: 600;
  font-size: 0.95rem;
}

.select-label i {
  color: #00c744;
  font-size: 0.9rem;
}

.custom-select {
  width: 100%;
  padding: 14px 18px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 0.95rem;
  color: #2d3748;
  background: white;
  transition: all 0.3s ease;
  cursor: pointer;
}

.custom-select:focus {
  outline: none;
  border-color: #00c744;
  box-shadow: 0 0 0 4px rgba(0, 199, 68, 0.1);
}

.custom-select:hover {
  border-color: #00c744;
}

/* ========== Plans Section ========== */
.plans-section {
  margin-top: 20px;
}

.plans-title {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #2d3748;
  font-size: 1.2rem;
  font-weight: 700;
  margin-bottom: 20px;
}

.plans-title i {
  color: #00c744;
}

.plans-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
}

.plans-loading,
.plans-empty {
  grid-column: 1 / -1;
  text-align: center;
  padding: 50px 20px;
  background: linear-gradient(135deg, #f8fffe 0%, #f0fdf4 100%);
  border-radius: 16px;
  border: 2px dashed rgba(0, 196, 68, 0.2);
}

.plans-loading p,
.plans-empty p {
  color: #718096;
  margin-top: 15px;
  font-weight: 500;
}

.plans-empty i {
  font-size: 40px;
  color: #00c744;
  opacity: 0.5;
}

/* ========== Plan Card ========== */
.plan-card {
  background: white;
  border: 2px solid rgba(0, 196, 68, 0.12);
  border-radius: 20px;
  padding: 24px;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
}

.plan-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 40px rgba(0, 199, 68, 0.15);
  border-color: rgba(0, 196, 68, 0.3);
}

.plan-header {
  text-align: center;
  padding-bottom: 20px;
  border-bottom: 1px solid #e2e8f0;
  margin-bottom: 20px;
}

.plan-name {
  color: #2d3748;
  font-size: 1.1rem;
  font-weight: 700;
  margin: 0 0 12px 0;
}

.plan-price {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 4px;
}

.price-value {
  font-size: 2.2rem;
  font-weight: 800;
  color: #00c744;
}

.price-value.free {
  font-size: 1.8rem;
  background: linear-gradient(135deg, #00c744, #00a83b);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.price-currency {
  font-size: 1rem;
  color: #718096;
  font-weight: 600;
}

.plan-details {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;
}

.detail-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: #f8fafc;
  border-radius: 10px;
}

.detail-item i {
  color: #00c744;
  font-size: 0.9rem;
  width: 20px;
}

.detail-label {
  flex: 1;
  color: #718096;
  font-size: 0.85rem;
}

.detail-value {
  color: #2d3748;
  font-weight: 600;
  font-size: 0.9rem;
}

.detail-badge {
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
}

.detail-badge.yes,
.detail-badge.active {
  background: rgba(0, 199, 68, 0.1);
  color: #00a83b;
}

.detail-badge.no,
.detail-badge.inactive {
  background: rgba(244, 106, 106, 0.1);
  color: #e55555;
}

.subscribe-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 14px 20px;
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 6px 20px rgba(0, 199, 68, 0.3);
}

.subscribe-btn i {
  color: white;
}

.subscribe-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 30px rgba(0, 199, 68, 0.4);
}

/* ========== Responsive ========== */
@media (max-width: 768px) {
  .hero-section {
    padding: 40px 15px;
    border-radius: 0 0 20px 20px;
  }

  .hero-icon {
    width: 60px;
    height: 60px;
  }

  .hero-icon i {
    font-size: 26px;
  }

  .hero-title {
    font-size: 1.5rem;
  }

  .main-card {
    border-radius: 18px;
  }

  .card-header-custom {
    padding: 18px 20px;
  }

  .card-body-custom {
    padding: 20px;
  }

  .plans-grid {
    grid-template-columns: 1fr;
  }
}

/* ========== Dark Mode Support ========== */
[data-bs-theme="dark"] .qbank-plan-page,
[data-theme="dark"] .qbank-plan-page,
.dark .qbank-plan-page {
  background: linear-gradient(180deg, #1a1a2e 0%, #16213e 100%);
}

[data-bs-theme="dark"] .hero-section,
[data-theme="dark"] .hero-section,
.dark .hero-section {
  background: linear-gradient(135deg, #0f5132 0%, #0a3622 100%);
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
}

[data-bs-theme="dark"] .loading-state,
[data-theme="dark"] .loading-state,
.dark .loading-state {
  background: #1e293b;
}

[data-bs-theme="dark"] .loading-text,
[data-theme="dark"] .loading-text,
.dark .loading-text {
  color: #4ade80;
}

[data-bs-theme="dark"] .main-card,
[data-theme="dark"] .main-card,
.dark .main-card {
  background: #1e293b;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
}

[data-bs-theme="dark"] .card-header-custom,
[data-theme="dark"] .card-header-custom,
.dark .card-header-custom {
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  border-bottom-color: rgba(255, 255, 255, 0.08);
}

[data-bs-theme="dark"] .header-icon,
[data-theme="dark"] .header-icon,
.dark .header-icon {
  background: linear-gradient(135deg, #0f5132 0%, #0a3622 100%);
}

[data-bs-theme="dark"] .header-text h3,
[data-theme="dark"] .header-text h3,
.dark .header-text h3 {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .header-text p,
[data-theme="dark"] .header-text p,
.dark .header-text p {
  color: #94a3b8;
}

[data-bs-theme="dark"] .select-label,
[data-theme="dark"] .select-label,
.dark .select-label {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .select-label i,
[data-theme="dark"] .select-label i,
.dark .select-label i {
  color: #4ade80;
}

[data-bs-theme="dark"] .custom-select,
[data-theme="dark"] .custom-select,
.dark .custom-select {
  background: #0f172a;
  border-color: rgba(255, 255, 255, 0.1);
  color: #f1f5f9;
}

[data-bs-theme="dark"] .custom-select:focus,
[data-theme="dark"] .custom-select:focus,
.dark .custom-select:focus {
  border-color: #4ade80;
  box-shadow: 0 0 0 4px rgba(74, 222, 128, 0.1);
}

[data-bs-theme="dark"] .plans-title,
[data-theme="dark"] .plans-title,
.dark .plans-title {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .plans-title i,
[data-theme="dark"] .plans-title i,
.dark .plans-title i {
  color: #4ade80;
}

[data-bs-theme="dark"] .plans-loading,
[data-bs-theme="dark"] .plans-empty,
[data-theme="dark"] .plans-loading,
[data-theme="dark"] .plans-empty,
.dark .plans-loading,
.dark .plans-empty {
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  border-color: rgba(255, 255, 255, 0.08);
}

[data-bs-theme="dark"] .plans-empty i,
[data-theme="dark"] .plans-empty i,
.dark .plans-empty i {
  color: #4ade80;
}

[data-bs-theme="dark"] .plan-card,
[data-theme="dark"] .plan-card,
.dark .plan-card {
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  border-color: rgba(255, 255, 255, 0.08);
}

[data-bs-theme="dark"] .plan-card:hover,
[data-theme="dark"] .plan-card:hover,
.dark .plan-card:hover {
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.4);
  border-color: rgba(74, 222, 128, 0.3);
}

[data-bs-theme="dark"] .plan-header,
[data-theme="dark"] .plan-header,
.dark .plan-header {
  border-bottom-color: rgba(255, 255, 255, 0.08);
}

[data-bs-theme="dark"] .plan-name,
[data-theme="dark"] .plan-name,
.dark .plan-name {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .price-value,
[data-theme="dark"] .price-value,
.dark .price-value {
  color: #4ade80;
}

[data-bs-theme="dark"] .price-currency,
[data-theme="dark"] .price-currency,
.dark .price-currency {
  color: #94a3b8;
}

[data-bs-theme="dark"] .detail-item,
[data-theme="dark"] .detail-item,
.dark .detail-item {
  background: rgba(255, 255, 255, 0.05);
}

[data-bs-theme="dark"] .detail-item i,
[data-theme="dark"] .detail-item i,
.dark .detail-item i {
  color: #4ade80;
}

[data-bs-theme="dark"] .detail-label,
[data-theme="dark"] .detail-label,
.dark .detail-label {
  color: #94a3b8;
}

[data-bs-theme="dark"] .detail-value,
[data-theme="dark"] .detail-value,
.dark .detail-value {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .detail-badge.yes,
[data-bs-theme="dark"] .detail-badge.active,
[data-theme="dark"] .detail-badge.yes,
[data-theme="dark"] .detail-badge.active,
.dark .detail-badge.yes,
.dark .detail-badge.active {
  background: rgba(74, 222, 128, 0.15);
  color: #4ade80;
}

[data-bs-theme="dark"] .detail-badge.no,
[data-bs-theme="dark"] .detail-badge.inactive,
[data-theme="dark"] .detail-badge.no,
[data-theme="dark"] .detail-badge.inactive,
.dark .detail-badge.no,
.dark .detail-badge.inactive {
  background: rgba(244, 106, 106, 0.15);
  color: #f87171;
}

[data-bs-theme="dark"] .subscribe-btn,
[data-theme="dark"] .subscribe-btn,
.dark .subscribe-btn {
  background: linear-gradient(135deg, #0f5132 0%, #0a3622 100%);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
}

/* ========== Beautiful SweetAlert Styling ========== */
:deep(.beautiful-swal-popup) {
  border-radius: 20px !important;
  padding: 32px !important;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15) !important;
  animation: swalSlideIn 0.3s ease-out !important;
}

:deep(.beautiful-swal-title) {
  font-size: 1.75rem !important;
  font-weight: 700 !important;
  color: #2d3748 !important;
  margin-bottom: 16px !important;
  padding: 0 !important;
}

:deep(.beautiful-swal-text) {
  font-size: 1rem !important;
  color: #64748b !important;
  line-height: 1.6 !important;
  margin: 0 !important;
}

:deep(.beautiful-swal-html) {
  margin: 0 !important;
  padding: 0 !important;
}

:deep(.beautiful-swal-confirm) {
  border-radius: 12px !important;
  padding: 12px 32px !important;
  font-size: 1rem !important;
  font-weight: 600 !important;
  box-shadow: 0 6px 20px rgba(0, 199, 68, 0.3) !important;
  transition: all 0.3s ease !important;
  border: none !important;
}

:deep(.beautiful-swal-confirm:hover) {
  transform: translateY(-2px) !important;
  box-shadow: 0 10px 30px rgba(0, 199, 68, 0.4) !important;
}

:deep(.beautiful-swal-cancel) {
  border-radius: 12px !important;
  padding: 12px 32px !important;
  font-size: 1rem !important;
  font-weight: 600 !important;
  box-shadow: 0 6px 20px rgba(244, 106, 106, 0.3) !important;
  transition: all 0.3s ease !important;
  border: none !important;
}

:deep(.beautiful-swal-cancel:hover) {
  transform: translateY(-2px) !important;
  box-shadow: 0 10px 30px rgba(244, 106, 106, 0.4) !important;
}

:deep(.swal2-icon.swal2-info) {
  border-color: #3b82f6 !important;
  color: #3b82f6 !important;
}

:deep(.swal2-icon.swal2-success) {
  border-color: #00c744 !important;
}

:deep(.swal2-icon.swal2-success [class^="swal2-success-line"]) {
  background-color: #00c744 !important;
}

:deep(.swal2-icon.swal2-success .swal2-success-ring) {
  border-color: rgba(0, 199, 68, 0.3) !important;
}

:deep(.already-subscribed-popup .swal2-icon.swal2-info) {
  transform: scale(1.1);
}

@keyframes swalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Dark Mode SweetAlert */
[data-bs-theme="dark"] :deep(.beautiful-swal-popup),
[data-theme="dark"] :deep(.beautiful-swal-popup),
.dark :deep(.beautiful-swal-popup) {
  background: #1e293b !important;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5) !important;
}

[data-bs-theme="dark"] :deep(.beautiful-swal-title),
[data-theme="dark"] :deep(.beautiful-swal-title),
.dark :deep(.beautiful-swal-title) {
  color: #f1f5f9 !important;
}

[data-bs-theme="dark"] :deep(.beautiful-swal-text),
[data-theme="dark"] :deep(.beautiful-swal-text),
.dark :deep(.beautiful-swal-text) {
  color: #94a3b8 !important;
}
</style>
